import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { login as apiLogin } from "../api/services";
import { startSession, isSessionExpired, clearSession } from "../utils/sessionCookie";

const AuthContext = createContext(null);

const SESSION_CHANNEL_NAME = "crm_session_channel";
const CHECK_INTERVAL_MS = 15000; // proactive expiry check every 15s

const normalizeUserRole = (userData) => {
  if (userData.role === "EMPLOYEE") {
    return { ...userData, role: "SALES_REP", originalRole: "EMPLOYEE" };
  }
  return userData;
};

function openChannel() {
  return typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(SESSION_CHANNEL_NAME) : null;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const channelRef = useRef(null);

  const clearAllAuthData = useCallback(() => {
    sessionStorage.removeItem("crm_user");
    clearSession();
    setUser(null);
  }, []);

  // Broadcast to other tabs. BroadcastChannel is primary; localStorage
  // 'storage' event is the fallback for older browsers.
  const notifyOtherTabs = useCallback((type) => {
    if (channelRef.current) {
      channelRef.current.postMessage({ type });
    } else {
      localStorage.setItem("crm_session_signal", JSON.stringify({ type, at: Date.now() }));
    }
  }, []);

  // Used for both manual logout and proactive expiry within THIS tab
  const forceLogout = useCallback((reason) => {
    clearAllAuthData();
    notifyOtherTabs(reason === "expired" ? "SESSION_EXPIRED" : "LOGOUT");
    const suffix = reason === "expired" ? "?reason=expired" : "";
    window.location.href = `/login${suffix}`;
  }, [clearAllAuthData, notifyOtherTabs]);

  // Restore + validate session on mount / page refresh
  useEffect(() => {
    const storedUser = sessionStorage.getItem("crm_user");
    if (storedUser) {
      if (isSessionExpired()) {
        clearAllAuthData();
      } else {
        setUser(normalizeUserRole(JSON.parse(storedUser)));
      }
    }
    setLoading(false);
  }, [clearAllAuthData]);

  // Proactive: catch expiry even if the user never navigates or refreshes
  useEffect(() => {
    const interval = setInterval(() => {
      if (sessionStorage.getItem("crm_user") && isSessionExpired()) {
        forceLogout("expired");
      }
    }, CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [forceLogout]);

  // Re-check the moment a tab becomes visible again (covers laptop sleep,
  // long-backgrounded tabs where the interval may have been throttled)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" &&
          sessionStorage.getItem("crm_user") && isSessionExpired()) {
        forceLogout("expired");
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [forceLogout]);

  // Cross-tab sync: another tab logged out or expired -> follow immediately
  useEffect(() => {
    channelRef.current = openChannel();

    if (channelRef.current) {
      channelRef.current.onmessage = (e) => {
        const type = e.data?.type;
        if (type === "SESSION_EXPIRED" || type === "LOGOUT") {
          clearAllAuthData();
          window.location.href = type === "SESSION_EXPIRED" ? "/login?reason=expired" : "/login";
        }
      };
    }

    const onStorage = (e) => {
      if (e.key === "crm_session_signal" && e.newValue) {
        const { type } = JSON.parse(e.newValue);
        clearAllAuthData();
        window.location.href = type === "SESSION_EXPIRED" ? "/login?reason=expired" : "/login";
      }
    };
    window.addEventListener("storage", onStorage);

    return () => {
      if (channelRef.current) channelRef.current.close();
      window.removeEventListener("storage", onStorage);
    };
  }, [clearAllAuthData]);

  const login = async (email, password) => {
    try {
      const response = await apiLogin({ email, password });

      if (response.requiresTwoFactor) {
        return {
          success: true,
          requiresTwoFactor: true,
          email: response.email || email,
          tempToken: response.tempToken,
          userId: response.userId,
        };
      }

      if (response.accessToken) {
        let userData = {
          email: response.email,
          token: response.accessToken,
          role: response.role,
          name: `${response.firstName} ${response.lastName}`,
          id: response.userId,
          superAdmin: response.superAdmin,
          originalRole: response.role,
        };

        userData = normalizeUserRole(userData);

        sessionStorage.setItem("crm_user", JSON.stringify(userData));
        startSession(); // sets the 3h expiry cookie — source of truth for session lifetime
        setUser(userData);
        return { success: true, user: userData };
      }

      return { success: false, message: "Invalid email or password." };
    } catch (error) {
      console.error("Login error:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Login failed. Please try again.",
      };
    }
  };

  const logout = () => {
    clearAllAuthData();
    notifyOtherTabs("LOGOUT");
  };

  // Exposed for ProtectedRoute (and anywhere else that needs a synchronous check)
  const isSessionValid = useCallback(() => {
    return !!sessionStorage.getItem("crm_user") && !isSessionExpired();
  }, []);

  const canAssignTasksToOthers = () => {
    if (!user) return false;
    const role = user.role;
    return role === "SUPER_ADMIN" || role === "ADMIN" || role === "MANAGER";
  };

  const isSalesRepOrEmployee = () => {
    if (!user) return false;
    return user.role === "SALES_REP" || user.originalRole === "EMPLOYEE";
  };

  return (
    <AuthContext.Provider value={{
      user, setUser, login, logout, loading,
      isSessionValid,
      canAssignTasksToOthers,
      isSalesRepOrEmployee,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};

export default AuthContext;